import { useSuspenseQuery } from '@tanstack/react-query';
import { CHILLDOC_MAX_BYTES, isChilldocName } from '@apm/contracts';
import { myFilesKeys } from '../api';
import { PreviewError } from './preview-error';

/** Trần xem trước văn bản/code/markdown/html — plan §2.6. */
export const TEXT_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Trần đọc một tài liệu soạn được: `.chilldoc` mang ảnh nhúng nên tới
 * `CHILLDOC_MAX_BYTES` (APM-CDIMG-a, plan-chilldoc-images §3.4); `.md`/`.txt` giữ 2 MB.
 */
export function documentReadMaxBytes(name: string): number {
  return isChilldocName(name) ? CHILLDOC_MAX_BYTES : TEXT_PREVIEW_MAX_BYTES;
}

/**
 * Trần xem trước bảng tính — cùng trần "Office" của plan §2.6 (csv thuộc nhóm
 * `spreadsheet`, xem chung lưới với xlsx ở `-e`).
 */
export const SHEET_PREVIEW_MAX_BYTES = 20 * 1024 * 1024;

/** Trần xem trước PDF và Office (docx, doc, xlsx, xls, pptx) — plan §2.6, APM-MYF-e. */
export const OFFICE_PREVIEW_MAX_BYTES = 20 * 1024 * 1024;

/**
 * Đọc nội dung chữ của một file bằng presigned GET — thẳng từ S3, không qua
 * API (luật #3), và **không** gửi header `Authorization` (`fetch` trần, không
 * phải `apiFetch`).
 *
 * Trần đếm **trong lúc** đọc: `sizeBytes` của hàng có thể thiếu (`null`), và
 * `Content-Length` có thể không có. Vượt trần ⇒ huỷ stream, ném `tooLarge`.
 *
 * `fetch` ném (khác với trả 4xx/5xx) gần như luôn là CORS của bucket chưa cho
 * `GET` từ origin của app (plan §2.7) — câu lỗi nói ra khả năng ấy.
 */
export async function fetchFileText(url: string, maxBytes: number): Promise<string> {
  const bytes = await fetchFileBytes(url, maxBytes);
  // TextDecoder bỏ BOM UTF-8 ở đầu (mặc định `ignoreBOM: false`) — csv xuất từ
  // Excel hay có BOM, để lại thì tiêu đề cột đầu mang một ký tự vô hình.
  return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Như `fetchFileText` nhưng trả byte thô — cho pdf và Office (APM-MYF-e). Cùng
 * đường đi (presigned GET, không `Authorization`) và cùng trần đếm trong lúc đọc.
 */
export async function fetchFileBytes(
  url: string,
  maxBytes: number,
  /**
   * Byte đã đọc / tổng (`Content-Length`; `null` khi không có) — thanh tiến độ của hộp
   * "Chuyển thành .chilldoc" (APM-CVT-a, plan-chilldoc-convert §3.3).
   */
  onProgress?: (done: number, total: number | null) => void,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  let response: Response;
  try {
    // Không có `signal` thì gọi đúng một tham số — không có gì ngoài URL đi theo request.
    response = await (signal ? fetch(url, { signal }) : fetch(url));
  } catch (caught) {
    if (signal?.aborted) throw caught;
    throw new PreviewError('fetchFailed');
  }
  if (!response.ok) throw new PreviewError('http', { status: response.status });

  const declared = Number(response.headers.get('content-length'));
  if (Number.isFinite(declared) && declared > maxBytes) {
    await response.body?.cancel();
    throw new PreviewError('tooLarge', { limit: maxBytes });
  }

  const total = Number.isFinite(declared) && declared > 0 ? declared : null;
  return readCapped(response, maxBytes, onProgress ? (done) => onProgress(done, total) : undefined);
}

async function readCapped(
  response: Response,
  maxBytes: number,
  onProgress?: (done: number) => void,
): Promise<Uint8Array> {
  if (!response.body) {
    const buffer = new Uint8Array(await response.arrayBuffer());
    if (buffer.byteLength > maxBytes) throw new PreviewError('tooLarge', { limit: maxBytes });
    onProgress?.(buffer.byteLength);
    return buffer;
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  onProgress?.(0);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > maxBytes) {
      await reader.cancel();
      throw new PreviewError('tooLarge', { limit: maxBytes });
    }
    chunks.push(value);
    onProgress?.(total);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}

/**
 * Nội dung chữ của file, kiểu Suspense: đang đọc ⇒ `Suspense` của khung xem
 * trước hiện chỗ chờ; lỗi ⇒ ranh giới lỗi hiện màn hình tải về kèm lý do.
 * Cache theo URL để tab "Mã nguồn"/"Trang" của html không đọc lại.
 */
export function useFileText(url: string, maxBytes: number): string {
  return useSuspenseQuery({
    queryKey: myFilesKeys.content(url),
    queryFn: () => fetchFileText(url, maxBytes),
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    retry: false,
  }).data;
}

/**
 * Byte thô của file, kiểu Suspense — cho pdf và Office (APM-MYF-e).
 *
 * ⚠️ Mảng trả về là **của cache**, dùng chung giữa các lần mở: thư viện nào
 * chuyển (transfer) buffer sang worker — pdf.js làm vậy — phải nhận một **bản
 * sao**, nếu không lần mở thứ hai gặp một buffer đã bị tách (`detached`), rỗng.
 * gcTime ngắn: tới 20 MB mỗi file, không giữ lâu sau khi đóng khung.
 */
export function useFileBytes(url: string, maxBytes: number): Uint8Array {
  return useSuspenseQuery({
    queryKey: [...myFilesKeys.content(url), 'bytes'],
    queryFn: () => fetchFileBytes(url, maxBytes),
    staleTime: Infinity,
    gcTime: 30_000,
    retry: false,
  }).data;
}
